import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Sparkles, Send, FlaskConical } from 'lucide-react';

export default function AdminNewsletter() {
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [testEmail, setTestEmail] = useState('');
    const [busy, setBusy] = useState('');

    const { data: stats, refetch } = useQuery({
        queryKey: ['admin-nl-stats'],
        queryFn: async () => (await api.get('/admin/newsletter/stats')).data,
        refetchInterval: 8000,
    });

    const { data: schedule, refetch: refetchSchedule } = useQuery({
        queryKey: ['admin-nl-schedule'],
        queryFn: async () => (await api.get('/admin/newsletter/schedule')).data,
    });

    const toggleSchedule = async () => {
        try {
            await api.post('/admin/newsletter/schedule', { enabled: !schedule?.enabled });
            refetchSchedule();
            toast.success(!schedule?.enabled ? 'Auto-send enabled — Sundays 8am ET' : 'Auto-send paused');
        } catch (e) {
            toast.error('Could not update the schedule');
        }
    };

    const draft = async () => {
        setBusy('draft');
        try {
            const { data } = await api.post('/admin/newsletter/draft');
            setSubject(data.subject);
            setBody(data.body_markdown);
            toast.success('Draft ready — review and edit before sending');
        } catch (e) {
            toast.error('Draft failed — try again');
        } finally { setBusy(''); }
    };

    const send = async (isTest) => {
        if (!subject.trim() || !body.trim()) { toast.error('Subject and body are required'); return; }
        if (isTest && !testEmail.trim()) { toast.error('Enter a test email address'); return; }
        if (!isTest && !window.confirm(`Send "${subject}" to all ${stats?.subscribers ?? '?'} subscribers?`)) return;
        setBusy(isTest ? 'test' : 'send');
        try {
            const { data } = await api.post('/admin/newsletter/send', {
                subject, body_markdown: body, ...(isTest ? { test_email: testEmail } : {}),
            });
            if (data.mode === 'test') {
                data.ok ? toast.success(`Test sent to ${testEmail}`) : toast.error(data.error || `Status: ${data.status}`);
            } else {
                toast.success(`Campaign started — sending to ${data.total} subscribers`);
                refetch();
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Send failed');
        } finally { setBusy(''); }
    };

    return (
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-14" data-testid="admin-newsletter">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline">Admin</div>
                    <h1 className="font-editorial text-[40px] tracking-editorial mt-2">The weekly briefing</h1>
                    <p className="mt-2 text-[14px] text-neutral-600">
                        {stats ? `${stats.subscribers} consented subscribers` : '…'} · sends from {stats?.from_email || 'sam@bestneighbourhoods.ca'} · unsubscribe link included automatically
                    </p>
                </div>
                <div className="flex gap-4 text-[13px]">
                    <Link to="/admin/submissions" className="link-editorial">Submissions</Link>
                    <Link to="/admin/leads" className="link-editorial">Leads</Link>
                </div>
            </div>

            {stats && !stats.email_configured && (
                <div className="mt-6 border-l-2 border-[#B87A2C] bg-[#B87A2C]/5 pl-4 py-3 text-[13.5px] text-neutral-700" data-testid="nl-not-configured">
                    <strong>Email notifications inactive.</strong> Subscribers are being saved with consent —
                    nothing sends until Resend is activated. You can still draft and prepare issues.
                </div>
            )}

            <div className="mt-8 border border-neutral-200 rounded-lg p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="nl-schedule-card">
                <div>
                    <div className="font-editorial text-[18px] tracking-editorial">Automatic Sunday send</div>
                    <p className="mt-1 text-[13px] text-neutral-600 max-w-xl">
                        Every Sunday at {schedule?.send_hour_et ?? 8}:00am ET the briefing drafts itself from live market
                        data and sends to all subscribers. {schedule?.last_result ? `Last run: ${schedule.last_result}.` : 'Has not run yet.'}
                        {stats && !stats.email_configured && ' Requires the Resend key to be active before it will send.'}
                    </p>
                </div>
                <button
                    onClick={toggleSchedule}
                    role="switch"
                    aria-checked={!!schedule?.enabled}
                    className={`shrink-0 inline-flex items-center gap-2.5 px-5 py-2.5 text-[13px] font-medium rounded-full border transition-colors ${
                        schedule?.enabled
                            ? 'bg-[#2F6B47] border-[#2F6B47] text-white hover:bg-[#255839]'
                            : 'border-neutral-300 text-neutral-600 hover:border-brand-navy hover:text-brand-navy'
                    }`}
                    data-testid="nl-schedule-toggle"
                >
                    <span className={`inline-block w-2 h-2 rounded-full ${schedule?.enabled ? 'bg-white' : 'bg-neutral-400'}`} />
                    {schedule?.enabled ? 'On — pause auto-send' : 'Paused — turn on auto-send'}
                </button>
            </div>

            <div className="mt-8 space-y-5">
                <button onClick={draft} disabled={busy === 'draft'} className="inline-flex items-center gap-2 bg-brand-navy text-white px-5 py-3 text-[13.5px] rounded-lg hover:bg-brand-gold hover:text-brand-navy transition-colors disabled:opacity-60" data-testid="nl-draft-btn">
                    <Sparkles className="w-4 h-4" /> {busy === 'draft' ? 'Drafting from live data…' : 'Draft this week with AI'}
                </button>

                <div>
                    <label htmlFor="nl-subject" className="bn-label block mb-1.5">Subject</label>
                    <input id="nl-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={180} placeholder="The neighbourhood letter — June 28" className="bn-input w-full" data-testid="nl-subject" />
                </div>
                <div>
                    <label htmlFor="nl-body" className="bn-label block mb-1.5">Body (markdown: ## headings, **bold**, - lists, [links](https://…))</label>
                    <textarea id="nl-body" value={body} onChange={(e) => setBody(e.target.value)} rows={16} placeholder="## One neighbourhood…" className="bn-input w-full font-mono-data text-[13px] leading-relaxed" data-testid="nl-body" />
                </div>

                <div className="flex flex-wrap items-center gap-3 border-t border-neutral-200 pt-5">
                    <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} type="email" placeholder="you@example.ca" className="bn-input text-[13px] w-60" data-testid="nl-test-email" aria-label="Test email address" />
                    <button onClick={() => send(true)} disabled={!!busy} className="inline-flex items-center gap-2 border border-brand-navy text-brand-navy px-5 py-2.5 text-[13.5px] rounded-lg hover:bg-brand-navy hover:text-white transition-colors disabled:opacity-60" data-testid="nl-send-test">
                        <FlaskConical className="w-4 h-4" /> Send test
                    </button>
                    <button onClick={() => send(false)} disabled={!!busy} className="inline-flex items-center gap-2 bg-brand-gold text-brand-navy px-6 py-2.5 text-[13.5px] font-semibold rounded-lg hover:bg-brand-navy hover:text-white transition-colors disabled:opacity-60" data-testid="nl-send-all">
                        <Send className="w-4 h-4" /> {busy === 'send' ? 'Starting…' : `Send to all ${stats?.subscribers ?? ''}`}
                    </button>
                </div>
            </div>

            <div className="mt-12">
                <div className="overline">Past campaigns</div>
                <div className="mt-4 border-y border-neutral-200 divide-y divide-neutral-200">
                    {(stats?.campaigns || []).length === 0 && <div className="py-8 text-neutral-500 text-[14px]">No campaigns sent yet.</div>}
                    {(stats?.campaigns || []).map((c) => (
                        <div key={c.campaign_id} className="py-4 flex items-center justify-between gap-4 flex-wrap">
                            <div>
                                <div className="font-editorial text-[16px] tracking-editorial">{c.subject}</div>
                                <div className="text-[12px] text-neutral-500 font-mono-data mt-0.5">{new Date(c.created_at).toLocaleString('en-CA')}</div>
                            </div>
                            <div className="text-[12.5px] font-mono-data text-neutral-600">
                                {c.sent}/{c.total} sent{c.failed ? ` · ${c.failed} failed` : ''} · {c.status}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
